import { motion } from 'framer-motion';
import { AudioLines, Cpu } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { DeviceMode } from '@/protocol/frames';
import { useSession } from '@/session/store';
import { Sparkle } from './icons/Sparkle';
import { ModelPicker } from './ModelPicker';
import { VoicePicker } from './VoicePicker';

const MODES: { mode: DeviceMode; title: string; blurb: string }[] = [
  { mode: 'DISH', title: 'DISH', blurb: 'Live TV, DVR and the full channel lineup.' },
  { mode: 'DANY', title: 'DANY', blurb: 'The Dany device experience, on the Dish tool set.' },
  { mode: 'SLING', title: 'Sling', blurb: 'Streaming packages and on-demand catalog.' },
];

export function ConnectScreen() {
  const connect = useSession((state) => state.connect);
  const connection = useSession((state) => state.connection);

  return (
    <main className="scroll-slim flex flex-1 items-center justify-center overflow-y-auto px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="border-accent/30 bg-accent/10 text-accent-soft grid size-11 place-items-center rounded-2xl border">
            <Sparkle className="size-5" />
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Choose a platform</h1>
            <p className="text-muted text-sm">
              Pick the experience to sign in on. The conversation starts once the session is open.
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {MODES.map(({ mode, title, blurb }) => (
            <button
              key={mode}
              type="button"
              disabled={connection !== 'idle'}
              onClick={() => connect(mode)}
              data-mode={mode}
              className={cn(
                // A button centres its content vertically when the grid stretches it, which lifts
                // the shorter cards' titles off the tallest card's line — so lay it out explicitly.
                'group border-line bg-surface/70 rounded-panel relative flex flex-col items-stretch justify-start overflow-hidden border p-5 text-center transition',
                'hover:border-accent/50 hover:bg-raised disabled:pointer-events-none disabled:opacity-50',
              )}
            >
              <span className="from-accent/12 pointer-events-none absolute inset-0 bg-gradient-to-br to-transparent opacity-0 transition group-hover:opacity-100" />
              {/* A fixed line box rather than the title's own leading: all three titles share one
                  horizontal line whatever the description under them does. */}
              <span className="relative flex h-7 items-center justify-center text-lg leading-none font-semibold tracking-tight">
                {title}
              </span>
              {/* Two lines are reserved, so a one-line description does not raise its card's text
                  above the others'. */}
              <span className="text-muted relative mt-1.5 block min-h-10 text-sm text-balance">
                {blurb}
              </span>
            </button>
          ))}
        </div>

        {/* Under the platforms rather than above them: the platform is the click that starts a
            session, and the model is what that session will be signed in on. The gateway lives in
            the settings dialog with the rest of the handshake. */}
        <div className="border-line-soft bg-surface/50 focus-within:border-accent/50 mt-6 flex items-center gap-3 rounded-xl border px-4 py-3 transition">
          <Cpu className="text-faint size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <label
              htmlFor="connect-model"
              className="text-faint block text-[11px] font-semibold tracking-widest uppercase"
            >
              Model
            </label>
            <ModelPicker id="connect-model" className="text-text -ml-0.5 py-0.5 text-sm" />
          </div>
        </div>

        {/* The voice the session speaks with, on the same terms as the model: both go into the
            sign-in handshake, both leave the backend on its own default when unnamed. */}
        <div className="border-line-soft bg-surface/50 focus-within:border-accent/50 mt-3 flex items-center gap-3 rounded-xl border px-4 py-3 transition">
          <AudioLines className="text-faint size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <label
              htmlFor="connect-voice"
              className="text-faint block text-[11px] font-semibold tracking-widest uppercase"
            >
              Voice
            </label>
            <VoicePicker id="connect-voice" className="text-text -ml-0.5 py-0.5 text-sm" />
          </div>
        </div>
      </motion.div>
    </main>
  );
}
